import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { InstanceId, ProductId, RoomId, ThemeTag } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave, SlotContent } from '@/core/persistence/schema/v1';
import { slotKey, slotParent, type SlotKey } from './placement/geometry';
import { addFriendship } from './friendship';
import type { HandlerEnv } from './placement/handlers';
import { accepts } from './placement/rules';
import { createRng, type Rng } from './rng';

const SHOP: RoomId = 'minimart';

// ---- displaying products ------------------------------------------------------------------------

/** A product on a slot is "đúng chỗ" when its homeTags `on:<surfaceTag>` matches the shelf (e.g. fruit in the basket). */
export function productRightSpot(
  content: ContentRegistry,
  s: GameSave,
  itemId: ProductId,
  parent: InstanceId,
): boolean {
  const def = content.items.get(itemId);
  const parentDef = content.items.get(s.inventory.instances[parent]?.itemId ?? 'dec_none');
  const tag = parentDef?.slots?.surfaceTag;
  if (!def || !tag) return false;
  return def.homeTags.some((rule) => rule.all.every((a) => a.kind === 'on' && a.surfaceTag === tag));
}

function checkSlot(
  content: ContentRegistry,
  s: GameSave,
  roomId: RoomId,
  itemId: ProductId,
  parent: InstanceId,
  index: number,
): 'ok' | 'NOT_FOUND' | 'NOT_ACCEPTED' | 'SLOT_OCCUPIED' {
  const room = s.rooms[roomId];
  const parentDef = content.items.get(s.inventory.instances[parent]?.itemId ?? 'dec_none');
  const def = content.items.get(itemId);
  if (!def || !parentDef?.slots || !(parent in room.placements)) return 'NOT_FOUND';
  if (index < 0 || index >= parentDef.slots.count) return 'NOT_ACCEPTED';
  if (!accepts(parentDef.slots.accepts, def)) return 'NOT_ACCEPTED';
  if (room.slots[slotKey(parent, index)]) return 'SLOT_OCCUPIED';
  return 'ok';
}

export function displayProduct(
  draft: GameSave,
  cmd: CommandOf<'displayProduct'>,
  env: HandlerEnv,
): CommandResult {
  if ((draft.inventory.productStacks[cmd.itemId] ?? 0) < 1) return { ok: false, error: 'INSUFFICIENT_STOCK' };
  const check = checkSlot(env.content, draft, cmd.roomId, cmd.itemId, cmd.parentInstanceId, cmd.slotIndex);
  if (check !== 'ok') return { ok: false, error: check === 'NOT_FOUND' ? 'NOT_FOUND' : check };
  draft.inventory.productStacks[cmd.itemId] = (draft.inventory.productStacks[cmd.itemId] ?? 0) - 1;
  const key = slotKey(cmd.parentInstanceId, cmd.slotIndex);
  draft.rooms[cmd.roomId].slots[key] = { kind: 'product', itemId: cmd.itemId };
  return {
    ok: true,
    events: [
      {
        type: 'PRODUCT_DISPLAYED',
        roomId: cmd.roomId,
        itemId: cmd.itemId,
        slotKey: key,
        rightSpot: productRightSpot(env.content, draft, cmd.itemId, cmd.parentInstanceId),
        origin: env.origin,
      },
      // Pâté on a Tiệm shelf: Boss Cat approves, once a day (§14.10).
      ...(cmd.itemId === 'prod_cat_pate' && cmd.roomId === SHOP && env.origin === 'player'
        ? addFriendship(draft, env.content, 'npc_boss_cat', 'pate', env.nowWallMs)
        : []),
    ],
  };
}

export function undisplayProduct(
  draft: GameSave,
  cmd: CommandOf<'undisplayProduct'>,
  env: HandlerEnv,
): CommandResult {
  const room = draft.rooms[cmd.roomId];
  const content = room.slots[cmd.slotKey];
  if (content?.kind !== 'product') return { ok: false, error: 'NOT_FOUND' };
  room.slots = Object.fromEntries(Object.entries(room.slots).filter(([k]) => k !== cmd.slotKey));
  draft.inventory.productStacks[content.itemId] = (draft.inventory.productStacks[content.itemId] ?? 0) + 1;
  return {
    ok: true,
    events: [
      {
        type: 'PRODUCT_UNDISPLAYED',
        roomId: cmd.roomId,
        itemId: content.itemId,
        slotKey: cmd.slotKey,
        to: 'bag',
        origin: env.origin,
      },
    ],
  };
}

export function moveProduct(draft: GameSave, cmd: CommandOf<'moveProduct'>, env: HandlerEnv): CommandResult {
  const room = draft.rooms[cmd.roomId];
  const content = room.slots[cmd.from];
  if (content?.kind !== 'product') return { ok: false, error: 'NOT_FOUND' };
  const check = checkSlot(
    env.content,
    draft,
    cmd.roomId,
    content.itemId,
    cmd.to.parentInstanceId,
    cmd.to.slotIndex,
  );
  if (check !== 'ok') return { ok: false, error: check === 'NOT_FOUND' ? 'NOT_FOUND' : check };
  const to = slotKey(cmd.to.parentInstanceId, cmd.to.slotIndex);
  room.slots = Object.fromEntries(Object.entries(room.slots).filter(([k]) => k !== cmd.from));
  room.slots[to] = content;
  return {
    ok: true,
    events: [
      {
        type: 'PRODUCT_DISPLAYED',
        roomId: cmd.roomId,
        itemId: content.itemId,
        slotKey: to,
        rightSpot: productRightSpot(env.content, draft, content.itemId, cmd.to.parentInstanceId),
        origin: env.origin,
      },
    ],
  };
}

// ---- open / close -------------------------------------------------------------------------------

export function toggleShopOpen(
  draft: GameSave,
  cmd: CommandOf<'toggleShopOpen'>,
  env: HandlerEnv,
): CommandResult {
  if (env.content.unlocks.size > 0 && !draft.unlockedIds.includes('unl_minimart'))
    return { ok: false, error: 'LOCKED' };
  if (draft.shop.isOpen === cmd.open) return { ok: true, events: [] };
  draft.shop.isOpen = cmd.open;
  // First customer comes quickly so the player sees the shop react (§14.3); closing lets current customers finish.
  draft.shop.sim.nextCustomerAtPlayMs = cmd.open ? draft.clock.playTimeMs + 3000 : null;
  return { ok: true, events: [{ type: 'SHOP_TOGGLED', open: cmd.open }] };
}

// ---- customer simulation (§14.3) ----------------------------------------------------------------

function sampleInterval(content: ContentRegistry, s: GameSave, rng: Rng): number {
  const b = content.balance;
  const level = s.coziness.minimart.bestLevel;
  const perk = s.shop.perks.includes('faster_customers') ? b.fasterCustomersCutSec : 0;
  const mean = Math.max(
    b.customerIntervalMinSec,
    b.customerIntervalSec - level * b.customerIntervalStepPerCozyLevelSec - perk,
  );
  return Math.round((mean + (rng.next() * 2 - 1) * b.customerIntervalJitterSec) * 1000);
}

export function shopThemes(content: ContentRegistry, s: GameSave): Set<ThemeTag> {
  const themes = new Set<ThemeTag>();
  const room = s.rooms[SHOP];
  const ids = [
    ...Object.keys(room.placements),
    ...Object.values(room.slots).flatMap((c) => (c.kind === 'instance' ? [c.instanceId] : [])),
  ] as InstanceId[];
  for (const id of ids) {
    const def = content.items.get(s.inventory.instances[id]?.itemId ?? 'dec_none');
    if (def && (def.kind === 'decor' || def.kind === 'furniture'))
      for (const t of def.themeTags) themes.add(t);
  }
  return themes;
}

export function isFavorite(content: ContentRegistry, npcId: string | undefined, itemId: ProductId): boolean {
  if (!npcId) return false;
  const npc = content.npcs.get(npcId);
  const def = content.items.get(itemId);
  if (!npc || !def) return false;
  return (
    npc.favorites.itemIds.includes(itemId) ||
    (def.productCategory !== undefined && npc.favorites.categories.includes(def.productCategory))
  );
}

/** Called from advanceTime. Deterministic given the save (its RNG state is stored in shop.sim). */
export function shopTick(draft: GameSave, env: HandlerEnv): DomainEvent[] {
  const { content } = env;
  const b = content.balance;
  const sim = draft.shop.sim;
  const events: DomainEvent[] = [];
  if (content.unlocks.size > 0 && !draft.unlockedIds.includes('unl_minimart')) return events;
  const t = draft.clock.playTimeMs;
  const rng = createRng(sim.rngState);

  // (a) Checkout customers whose time has come.
  const due = sim.active
    .filter((c) => c.checkoutAtPlayMs <= t)
    .sort((x, y) => x.checkoutAtPlayMs - y.checkoutAtPlayMs);
  for (const c of due) {
    const lines = new Map<ProductId, number>();
    for (const item of c.basket) lines.set(item.itemId, (lines.get(item.itemId) ?? 0) + 1);
    const saleLines = [...lines].map(([itemId, qty]) => ({
      itemId,
      qty,
      unitPrice: content.items.get(itemId)?.sellPrice ?? 0,
    }));
    const total = saleLines.reduce((sum, l) => sum + l.qty * l.unitPrice, 0);
    const txId = `tx_sale_${c.customerId}` as const;
    draft.wallet.coins += total;
    const ls = draft.shop.ledgerSummary;
    ls.revenueTotal += total;
    ls.salesCount += 1;
    for (const l of saleLines) {
      ls.unitsSoldTotal += l.qty;
      const bp = (ls.byProduct[l.itemId] ??= { sold: 0, revenue: 0 });
      bp.sold += l.qty;
      bp.revenue += l.qty * l.unitPrice;
    }
    env.ledger?.push({
      txId,
      saveId: draft.saveId,
      atWallMs: env.nowWallMs,
      atPlayMs: t,
      kind: 'sale',
      coinsDelta: total,
      balanceAfter: draft.wallet.coins,
      refs: { customerId: c.customerId, units: c.basket.length },
    });
    const favoriteMatched = saleLines.some((l) => isFavorite(content, c.visitorNpcId, l.itemId));
    events.push({
      type: 'SALE_COMPLETED',
      txId,
      customerId: c.customerId,
      ...(c.visitorNpcId ? { visitorNpcId: c.visitorNpcId } : {}),
      lines: saleLines,
      total,
      favoriteMatched,
    });
    // A neighbour buying a favourite warms up the friendship a little (daily cap, §14.10).
    if (favoriteMatched && c.visitorNpcId)
      events.push(...addFriendship(draft, content, c.visitorNpcId, 'favoriteBuy', env.nowWallMs));
  }
  if (due.length > 0) sim.active = sim.active.filter((c) => !due.includes(c));

  // (b) New customers while the door is open.
  if (draft.shop.isOpen) {
    sim.nextCustomerAtPlayMs ??= t + sampleInterval(content, draft, rng);
    while (sim.nextCustomerAtPlayMs <= t) {
      if (sim.active.length >= b.maxConcurrentCustomers) break; // arrives a little later, never lost
      const arriveAt = sim.nextCustomerAtPlayMs;
      sim.customerSeq += 1;
      const customerId = `c_${String(sim.customerSeq)}` as const;
      const met = [...content.npcs.values()].filter(
        (n) => n.kind === 'human' && n.canShop && draft.flags[n.metFlag],
      );
      const visitor = met.length > 0 && rng.next() < b.namedVisitorChance ? rng.pick(met).id : undefined;
      const room = draft.rooms[SHOP];
      const units = (Object.entries(room.slots) as [SlotKey, SlotContent][])
        .filter((e): e is [SlotKey, { kind: 'product'; itemId: ProductId }] => e[1].kind === 'product')
        .map(([key, c]) => ({ key, itemId: c.itemId }))
        .sort((x, y) => x.key.localeCompare(y.key));
      if (units.length === 0) {
        events.push({
          type: 'CUSTOMER_LEFT_EMPTY',
          customerId,
          ...(visitor ? { visitorNpcId: visitor } : {}),
        });
      } else {
        const weights = draft.shop.perks.includes('regulars_buy_more')
          ? b.regularsBuyQtyWeights
          : b.buyQtyWeights;
        const qty = rng.weighted([1, 2, 3], (q) => weights[q - 1] ?? 0);
        const themes = shopThemes(content, draft);
        const cw = b.customerWeights;
        const basket: { itemId: ProductId; fromSlot: SlotKey }[] = [];
        for (let i = 0; i < Math.min(qty, units.length + basket.length) && units.length > 0; i++) {
          const pick = rng.weighted(units, (u) => {
            const def = content.items.get(u.itemId);
            const match = def ? def.themeTags.filter((tag) => themes.has(tag)).length : 0;
            return (
              cw.base + match * cw.perThemeMatch + (isFavorite(content, visitor, u.itemId) ? cw.favorite : 0)
            );
          });
          units.splice(units.indexOf(pick), 1);
          room.slots = Object.fromEntries(Object.entries(room.slots).filter(([k]) => k !== pick.key));
          basket.push({ itemId: pick.itemId, fromSlot: pick.key });
          events.push({
            type: 'PRODUCT_UNDISPLAYED',
            roomId: SHOP,
            itemId: pick.itemId,
            slotKey: pick.key,
            to: 'customer',
            origin: 'system',
          });
        }
        const browse =
          b.customerBrowseSec.min + rng.next() * (b.customerBrowseSec.max - b.customerBrowseSec.min);
        sim.active.push({
          customerId,
          ...(visitor ? { visitorNpcId: visitor } : {}),
          phase: 'browsing',
          basket,
          checkoutAtPlayMs: arriveAt + Math.round(browse * 1000),
        });
        events.push({ type: 'CUSTOMER_ARRIVED', customerId, ...(visitor ? { visitorNpcId: visitor } : {}) });
        events.push({ type: 'CUSTOMER_PICKED', customerId, items: basket });
      }
      sim.nextCustomerAtPlayMs += sampleInterval(content, draft, rng);
    }
  }
  sim.rngState = rng.state;
  return events;
}

/**
 * On load: customers caught mid-visit put their items back (on the shelf if the slot is still free,
 * otherwise in the bag). Sales never complete "while the app was closed" (plan §3.3).
 */
export function resetShopOnBoot(draft: GameSave): void {
  const room = draft.rooms[SHOP];
  for (const c of draft.shop.sim.active) {
    for (const item of c.basket) {
      const parent = slotParent(item.fromSlot);
      if (!room.slots[item.fromSlot] && parent in room.placements)
        room.slots[item.fromSlot] = { kind: 'product', itemId: item.itemId };
      else draft.inventory.productStacks[item.itemId] = (draft.inventory.productStacks[item.itemId] ?? 0) + 1;
    }
  }
  draft.shop.sim.active = [];
  draft.shop.sim.nextCustomerAtPlayMs = null;
}
